import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Button, Card, InputNumber, message, Select, Space, Table, Tag, Typography} from "antd";
import {SaveOutlined} from "@ant-design/icons";
import {
    getDisciplineGrades,
    batchAddGrades,
    getGroupStudents
} from "../../../../../../store/slices/MicroqualificationGroupsSlice";
import {LocalName} from "../../../../../../utils/LocalName";
import IntlMessage from "../../../../../../components/IntlMessage/IntlMessage";

const {Text} = Typography;

const GradesTab = ({groupId, group}) => {
    const dispatch = useDispatch();
    const [selectedDiscipline, setSelectedDiscipline] = useState(null);
    const [grades, setGrades] = useState({});
    const [loading, setLoading] = useState(false);

    const {disciplineGrades, gradesLoading, students} = useSelector(state => state.microqualificationGroups);

    useEffect(() => {
        if (groupId) {
            dispatch(getGroupStudents(groupId));
        }
    }, [groupId, dispatch]);

    useEffect(() => {
        if (group?.disciplines?.length > 0 && !selectedDiscipline) {
            setSelectedDiscipline(group.disciplines[0].id);
        }
    }, [group, selectedDiscipline]);

    useEffect(() => {
        if (selectedDiscipline && groupId) {
            dispatch(getDisciplineGrades({groupId, disciplineId: selectedDiscipline}));
        }
    }, [selectedDiscipline, groupId, dispatch]);

    useEffect(() => {
        if (disciplineGrades && students) {
            const gradesMap = {};
            disciplineGrades.forEach(grade => {
                gradesMap[grade.studentId] = grade.grade;
            });
            setGrades(gradesMap);
        }
    }, [disciplineGrades, students]);

    const handleGradeChange = (studentId, value) => {
        setGrades(prev => ({
            ...prev,
            [studentId]: value
        }));
    };

    const handleSaveGrades = async () => {
        setLoading(true);
        try {
            const gradesData = {
                grades: students
                    .filter(student => grades[student.id] !== undefined && grades[student.id] !== null)
                    .map(student => ({
                        studentId: student.id,
                        grade: grades[student.id]
                    }))
            };

            await dispatch(batchAddGrades({
                groupId,
                disciplineId: selectedDiscipline,
                gradesData
            })).unwrap();
            message.success(<IntlMessage id="mqGroups.gradesSaved"/>);
        } catch (error) {
            message.error(<IntlMessage id="mqGroups.failedToSaveGrades"/>);
        } finally {
            setLoading(false);
        }
    };

    const getGradeFromPercentage = (percentage) => {
        if (percentage === null || percentage === undefined) return '-';

        if (percentage >= 95) return 'A (4.0)';
        if (percentage >= 90) return 'A- (3.67)';
        if (percentage >= 85) return 'B+ (3.33)';
        if (percentage >= 80) return 'B (3.0)';
        if (percentage >= 75) return 'B- (2.67)';
        if (percentage >= 70) return 'C+ (2.33)';
        if (percentage >= 65) return 'C (2.0)';
        if (percentage >= 60) return 'C- (1.67)';
        if (percentage >= 55) return 'D+ (1.33)';
        if (percentage >= 50) return 'D (1.0)';
        if (percentage >= 25) return 'FX (0.5)';
        return 'F (0.0)';
    };

    const columns = [
        {
            title: '#',
            key: 'index',
            width: '5%',
            render: (_, __, index) => index + 1
        },
        {
            title: <IntlMessage id="mqGroups.studentName"/>,
            key: 'studentName',
            width: '30%',
            render: (_, record) => (
                <Text>{record.fullNameEng}</Text>
            )
        },
        {
            title: <IntlMessage id="mqGroups.grade"/>,
            key: 'grade',
            width: '20%',
            align: 'center',
            render: (_, record) => {
                const grade = getGradeFromPercentage(grades[record.id]);
                const gradeValue = grade.split(' ')[0];
                return grade !== '-' ? (
                    <Tag color={gradeValue.startsWith('A') ? 'green' : gradeValue.startsWith('B') ? 'blue' : gradeValue.startsWith('C') ? 'orange' : 'red'}>
                        {grade}
                    </Tag>
                ) : <Text type="secondary">-</Text>;
            }
        },
        {
            title: <IntlMessage id="mqGroups.percentageScore"/>,
            key: 'grade',
            width: '25%',
            render: (_, record) => (
                <InputNumber
                    style={{width: '100%'}}
                    min={0}
                    max={100}
                    placeholder="0-100"
                    value={grades[record.id]}
                    onChange={(value) => handleGradeChange(record.id, value)}
                />
            )
        }
    ];

    if (!group?.disciplines || group.disciplines.length === 0) {
        return (
            <Card>
                <Text type="secondary"><IntlMessage id="mqGroups.noDisciplines"/></Text>
            </Card>
        );
    }

    return (
        <Card>
            <Space direction="vertical" size="large" style={{width: '100%'}}>
                <Space style={{width: '100%', justifyContent: 'space-between'}}>
                    <Space>
                        <Text strong><IntlMessage id="mqGroups.selectDiscipline"/>:</Text>
                        <Select
                            style={{width: 400}}
                            value={selectedDiscipline}
                            onChange={setSelectedDiscipline}
                        >
                            {group.disciplines.map(discipline => (
                                <Select.Option key={discipline.id} value={discipline.id}>
                                    <div>
                                        <Text>{discipline.code} - {LocalName.getName(discipline)}</Text>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Space>
                    <Button
                        type="primary"
                        icon={<SaveOutlined/>}
                        onClick={handleSaveGrades}
                        loading={loading}
                    >
                        <IntlMessage id="mqGroups.saveAllGrades"/>
                    </Button>
                </Space>

                <Table
                    columns={columns}
                    dataSource={students}
                    loading={gradesLoading}
                    rowKey="id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => <IntlMessage id="totalStudents" values={{count: total}}/>
                    }}
                />
            </Space>
        </Card>
    );
};

export default GradesTab;
